<template>
  <div class="operations-live-tracking" dir="rtl">
    <vue-element-loading :active="isLoading && !isPolling" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== 1. PAGE HEADER ==================== -->
    <div class="op-header-row mb-5">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="op-badge-section">
            <v-icon x-small color="#082C69" class="me-1">mdi-radar</v-icon>
            مركز العمليات
          </span>

          <!-- Live Status Pill -->
          <div class="op-live-pill d-inline-flex align-center">
            <span class="op-pulse-dot me-1"></span>
            <span class="font-weight-bold">مباشر</span>
          </div>
        </div>

        <h1 class="op-main-title">مركز التتبع المباشر</h1>
        <p class="op-subtitle">مراقبة جميع الرحلات والمركبات في الوقت الفعلي على الخريطة</p>
      </div>

      <!-- Header Actions -->
      <div class="op-header-actions d-flex align-center gap-2 flex-wrap">
        <span class="op-last-updated text-caption text-secondary">
          آخر تحديث: <strong class="text-dark">{{ lastUpdatedFormatted }}</strong>
        </span>

        <v-btn
          depressed
          class="op-btn-refresh"
          :loading="isLoading"
          :disabled="isLoading"
          @click="fetchLiveOverview(true)"
        >
          <v-icon size="18" color="#082C69">mdi-refresh</v-icon>
          <span class="ms-1 font-weight-bold">تحديث الآن</span>
        </v-btn>
      </div>
    </div>

    <!-- ==================== NO ASSIGNED GROUPS STATE ==================== -->
    <div v-if="!isLoading && !overview.has_assignments" class="op-unassigned-card text-center py-12 px-6">
      <div class="op-empty-icon-circle mx-auto mb-4">
        <v-icon size="48" color="#64748B">mdi-folder-alert-outline</v-icon>
      </div>
      <h2 class="op-empty-title mb-2">لا توجد مجموعات تشغيل مسندة إليك حالياً</h2>
      <p class="op-empty-desc mx-auto">
        حسابك التشغيلي نشط، ولكن لم يتم إسناد أي مجموعة تشغيل إليك بعد. يرجى التواصل مع إدارة النظام لتعيين مجموعات التشغيل الخاصة بك لتتمكن من متابعة التتبع المباشر للرحلات.
      </p>
    </div>

    <!-- ==================== MAIN CONTENT ==================== -->
    <div v-else>
      <!-- ==================== 2. TOP FILTER BAR ==================== -->
      <v-card class="op-filter-card mb-5 elevation-1 rounded-lg">
        <div class="pa-4">
          <v-row dense align="center">
            <!-- Search -->
            <v-col cols="12" md="3" sm="6">
              <v-text-field
                v-model="filters.search"
                dense
                outlined
                hide-details
                placeholder="ابحث برقم الرحلة أو اسم المسار أو رقم المركبة..."
                prepend-inner-icon="mdi-magnify"
                class="op-filter-input"
                clearable
                @input="debounceSearch"
              ></v-text-field>
            </v-col>

            <!-- Status Filter -->
            <v-col cols="6" md="2" sm="3">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                dense
                outlined
                hide-details
                item-text="label"
                item-value="id"
                label="الحالات"
                class="op-filter-input"
                @change="applyFilters"
              ></v-select>
            </v-col>

            <!-- Driver Filter -->
            <v-col cols="6" md="2" sm="3">
              <v-select
                v-model="filters.driver_id"
                :items="driverOptions"
                dense
                outlined
                hide-details
                item-text="label"
                item-value="id"
                label="كل السائقين"
                class="op-filter-input"
                @change="applyFilters"
              ></v-select>
            </v-col>

            <!-- Vehicle Filter -->
            <v-col cols="6" md="2" sm="3">
              <v-select
                v-model="filters.vehicle_id"
                :items="vehicleOptions"
                dense
                outlined
                hide-details
                item-text="label"
                item-value="id"
                label="كل المركبات"
                class="op-filter-input"
                @change="applyFilters"
              ></v-select>
            </v-col>

            <!-- Group Filter -->
            <v-col cols="6" md="2" sm="3">
              <v-select
                v-model="filters.group_id"
                :items="groupOptions"
                dense
                outlined
                hide-details
                item-text="label"
                item-value="id"
                label="كل المجموعات"
                class="op-filter-input"
                @change="applyFilters"
              ></v-select>
            </v-col>

            <!-- Reset Filters -->
            <v-col cols="12" md="1" sm="12" class="text-left text-sm-right">
              <v-btn
                text
                small
                color="secondary"
                class="font-weight-bold px-2"
                :disabled="!hasActiveFilters"
                @click="resetFilters"
              >
                <v-icon small left>mdi-filter-remove-outline</v-icon>
                إعادة ضبط
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card>

      <!-- ==================== 3. 6 COMPACT KPI CARDS ==================== -->
      <v-row dense class="op-kpi-row mb-5">
        <!-- 1. إجمالي المركبات العاملة -->
        <v-col cols="6" sm="4" md="2">
          <div class="op-kpi-card kpi-active-buses">
            <div class="kpi-icon-wrap">
              <v-icon size="22" color="#082C69">mdi-bus-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-value text-primary">{{ overview.kpis.active_vehicles_count }}</div>
              <div class="kpi-label">إجمالي المركبات العاملة</div>
            </div>
          </div>
        </v-col>

        <!-- 2. رحلات غير متصلة -->
        <v-col cols="6" sm="4" md="2">
          <div class="op-kpi-card kpi-disconnected">
            <div class="kpi-icon-wrap">
              <v-icon size="22" color="#64748B">mdi-wifi-off</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-value text-slate">{{ overview.kpis.disconnected_count }}</div>
              <div class="kpi-label">رحلات غير متصلة</div>
            </div>
          </div>
        </v-col>

        <!-- 3. رحلات متأخرة -->
        <v-col cols="6" sm="4" md="2">
          <div class="op-kpi-card kpi-delayed">
            <div class="kpi-icon-wrap">
              <v-icon size="22" color="#D97706">mdi-clock-alert-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-value text-amber">{{ overview.kpis.delayed_count }}</div>
              <div class="kpi-label">رحلات متأخرة</div>
            </div>
          </div>
        </v-col>

        <!-- 4. الرحلات المكتملة اليوم -->
        <v-col cols="6" sm="4" md="2">
          <div class="op-kpi-card kpi-completed">
            <div class="kpi-icon-wrap">
              <v-icon size="22" color="#059669">mdi-check-circle-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-value text-emerald">{{ overview.kpis.completed_today_count }}</div>
              <div class="kpi-label">الرحلات المكتملة اليوم</div>
            </div>
          </div>
        </v-col>

        <!-- 5. في انتظار الانطلاق -->
        <v-col cols="6" sm="4" md="2">
          <div class="op-kpi-card kpi-upcoming">
            <div class="kpi-icon-wrap">
              <v-icon size="22" color="#0284C7">mdi-timer-sand</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-value text-sky">{{ overview.kpis.upcoming_count }}</div>
              <div class="kpi-label">في انتظار الانطلاق</div>
            </div>
          </div>
        </v-col>

        <!-- 6. الرحلات الجارية الآن -->
        <v-col cols="6" sm="4" md="2">
          <div class="op-kpi-card kpi-running active-pulse">
            <div class="kpi-icon-wrap">
              <v-icon size="22" color="#2563EB">mdi-radar</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-value text-blue">{{ overview.kpis.running_count }}</div>
              <div class="kpi-label">الرحلات الجارية الآن</div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ==================== 4. MAIN WORKSPACE: MAP (68%) & RUNNING TRIPS (32%) ==================== -->
      <v-row dense class="mb-5">
        <!-- MAP SECTION (~68% on Desktop) -->
        <v-col cols="12" md="8">
          <v-card class="op-map-container elevation-1 rounded-lg overflow-hidden position-relative">
            <!-- Map Top Bar Overlay -->
            <div class="op-map-header-bar d-flex align-center justify-space-between px-4 py-2">
              <div class="d-flex align-center gap-2">
                <v-icon small color="#082C69">mdi-map-marker-path</v-icon>
                <span class="font-weight-bold text-caption text-dark">
                  خريطة التتبع المباشر
                  <span v-if="selectedTrip">
                    ({{ selectedTrip.planned_trip_code }} - {{ selectedTrip.route_name }})
                  </span>
                </span>
              </div>

              <!-- Map Legend Badges -->
              <div class="d-none d-sm-flex align-center gap-3 text-caption">
                <span class="d-flex align-center">
                  <v-icon x-small color="#059669" class="me-1">mdi-bus</v-icon>
                  <span>على الطريق</span>
                </span>
                <span class="d-flex align-center">
                  <v-icon x-small color="#D97706" class="me-1">mdi-bus</v-icon>
                  <span>تأخير</span>
                </span>
                <span class="d-flex align-center">
                  <v-icon x-small color="#64748B" class="me-1">mdi-bus</v-icon>
                  <span>غير متصلة</span>
                </span>
              </div>
            </div>

            <!-- Map Empty Overlay Badge when runningTrips.length === 0 -->
            <div v-if="runningTrips.length === 0" class="op-map-empty-badge">
              <v-icon small color="#082C69" class="me-1">mdi-information-outline</v-icon>
              <span>لا توجد رحلات جارية حالياً</span>
            </div>

            <!-- Google Map Loader -->
            <GoogleMapLoader
              :apiKey="googleMapsApiKey"
              :center="mapCenter"
              :zoom="mapZoom"
              :markers="activeMapMarkers"
              :polylines="activePolylines"
              height="530px"
              ref="trackingMap"
              @marker-click="handleMarkerClick"
            />
          </v-card>
        </v-col>

        <!-- RUNNING TRIPS PANEL (~32% on Desktop) -->
        <v-col cols="12" md="4">
          <v-card class="op-trips-sidebar elevation-1 rounded-lg d-flex flex-column" style="height: 575px;">
            <!-- Sidebar Header -->
            <div class="sidebar-header px-4 pt-3 pb-2 border-bottom">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-icon small color="#082C69" class="me-1">mdi-bus-clock</v-icon>
                  <span class="font-weight-bold text-subtitle-2 text-dark">الرحلات الجارية الآن</span>
                </div>
                <v-chip x-small color="#082C69" text-color="white" class="font-weight-bold">
                  {{ filteredRunningTrips.length }}
                </v-chip>
              </div>

              <!-- Sidebar Quick Tabs (الكل، على الطريق، تأخير) -->
              <div class="sidebar-tabs d-flex gap-1">
                <button
                  type="button"
                  :class="['sidebar-tab-btn', sidebarTab === 'all' ? 'active' : '']"
                  @click="sidebarTab = 'all'"
                >
                  الكل ({{ runningTrips.length }})
                </button>
                <button
                  type="button"
                  :class="['sidebar-tab-btn', sidebarTab === 'on_time' ? 'active' : '']"
                  @click="sidebarTab = 'on_time'"
                >
                  على الطريق ({{ onTimeRunningTripsCount }})
                </button>
                <button
                  type="button"
                  :class="['sidebar-tab-btn', sidebarTab === 'delayed' ? 'active' : '']"
                  @click="sidebarTab = 'delayed'"
                >
                  تأخير ({{ delayedRunningTripsCount }})
                </button>
              </div>
            </div>

            <!-- Running Trips Scrollable List -->
            <div class="sidebar-trips-list flex-grow-1 overflow-y-auto px-3 py-2">
              <template v-if="filteredRunningTrips.length > 0">
                <div
                  v-for="trip in filteredRunningTrips"
                  :key="'rt-' + trip.id"
                  :class="[
                    'op-running-trip-card',
                    selectedTripId === trip.id ? 'is-selected' : '',
                    trip.is_delayed ? 'is-delayed' : (trip.is_disconnected ? 'is-disconnected' : 'is-normal-active')
                  ]"
                  @click="selectTrip(trip.id)"
                >
                  <!-- Card Header: Code & Status & Delay Badges -->
                  <div class="d-flex align-center justify-space-between mb-1">
                    <div class="d-flex align-center gap-1 flex-wrap">
                      <span class="trip-code-pill font-weight-black">{{ trip.planned_trip_code }}</span>
                      <span
                        v-if="trip.is_delayed"
                        class="op-delay-type-badge font-weight-bold"
                        :class="trip.delay_type === 'delayed_start' ? 'badge-delay-start' : 'badge-delay-exec'"
                      >
                        {{ trip.delay_type_label || (trip.delay_type === 'delayed_start' ? 'تأخر في بدء الرحلة' : 'تأخر أثناء تنفيذ الرحلة') }}
                      </span>
                    </div>
                    <div class="d-flex align-center gap-1">
                      <span v-if="trip.is_delayed" class="op-delay-pill-amber font-weight-bold">
                        {{ trip.delay_label }}
                      </span>
                      <span :class="['trip-status-pill', `status-${trip.status_key}`]">
                        {{ trip.status_label }}
                      </span>
                    </div>
                  </div>

                  <!-- Route Name -->
                  <div class="trip-route-name text-truncate font-weight-bold text-dark mb-1" :title="trip.route_name">
                    {{ trip.route_name }}
                  </div>

                  <!-- Driver & Vehicle -->
                  <div class="d-flex align-center justify-space-between text-caption text-secondary mb-2">
                    <div class="d-flex align-center text-truncate me-2">
                      <v-icon x-small color="#64748B" class="me-1">mdi-card-account-details-outline</v-icon>
                      <span class="text-truncate">{{ trip.driver ? trip.driver.name : 'سائق غير محدد' }}</span>
                    </div>
                    <div class="d-flex align-center text-no-wrap">
                      <v-icon x-small color="#64748B" class="me-1">mdi-bus</v-icon>
                      <span>{{ trip.vehicle ? trip.vehicle.plate : 'مركبة غير محددة' }}</span>
                    </div>
                  </div>

                  <!-- Progress Bar -->
                  <div class="trip-progress-wrap mb-1">
                    <div class="d-flex justify-space-between text-caption mb-1">
                      <span class="text-caption text-secondary font-weight-bold">
                        محطة {{ trip.completed_stops_count }} من {{ trip.total_stops_count }}
                      </span>
                      <span class="font-weight-black text-primary">{{ trip.progress_percentage }}%</span>
                    </div>
                    <v-progress-linear
                      :value="trip.progress_percentage"
                      height="6"
                      rounded
                      :color="trip.status_key === 'delayed' ? '#D97706' : '#082C69'"
                      background-color="#E2E8F0"
                    ></v-progress-linear>
                  </div>

                  <!-- Footer: Freshness & Speed -->
                  <div class="d-flex align-center justify-space-between text-caption text-muted mt-2">
                    <span class="d-flex align-center">
                      <v-icon x-small :color="trip.is_disconnected ? '#94A3B8' : '#059669'" class="me-1">
                        {{ trip.is_disconnected ? 'mdi-wifi-off' : 'mdi-wifi' }}
                      </v-icon>
                      {{ trip.freshness_text }}
                    </span>
                    <span v-if="trip.location && trip.location.speed" class="font-weight-bold text-dark">
                      {{ Math.round(trip.location.speed) }} كم/س
                    </span>
                  </div>
                </div>
              </template>

              <!-- Empty State in Sidebar -->
              <div v-else class="text-center py-10 px-4">
                <v-icon size="40" color="#CBD5E1" class="mb-2">mdi-bus-stop</v-icon>
                <div class="text-subtitle-2 font-weight-bold text-secondary mb-1">لا توجد رحلات جارية مطابقة</div>
                <div class="text-caption text-muted">لا توجد رحلات جارية تتطابق مع معايير التصفية الحالية</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ==================== 5. BOTTOM 3 CARDS: SELECTED TRIP DETAILS ==================== -->
      <div v-if="selectedTrip" class="op-trip-details-section">
        <vue-element-loading :active="isLoadingDetails" spinner="bar-fade-scale" color="#082C69" />

        <v-row dense>
          <!-- CARD 1: تفاصيل الرحلة الحالية -->
          <v-col cols="12" md="4">
            <v-card class="op-detail-card elevation-1 rounded-lg h-100 pa-4">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#082C69" class="me-2" size="20">mdi-information-outline</v-icon>
                  <span class="font-weight-bold text-subtitle-2 text-dark">تفاصيل الرحلة الحالية</span>
                </div>
                <span class="trip-code-pill font-weight-black">{{ selectedTrip.planned_trip_code }}</span>
              </div>

              <!-- Route Title & Status -->
              <div class="mb-3">
                <div class="text-subtitle-1 font-weight-black text-dark mb-1">
                  {{ selectedTrip.route_name }}
                </div>
                <div class="d-flex align-center gap-2 flex-wrap">
                  <span :class="['trip-status-pill', `status-${selectedTrip.status_key}`]">
                    {{ selectedTrip.status_label }}
                  </span>
                  <span v-if="selectedTrip.is_delayed" class="op-delay-pill">
                    {{ selectedTrip.delay_label }}
                  </span>
                </div>
              </div>

              <v-divider class="my-3"></v-divider>

              <!-- Driver Information -->
              <div class="detail-row d-flex align-center justify-space-between mb-2">
                <span class="text-caption text-secondary">السائق:</span>
                <span class="font-weight-bold text-dark d-flex align-center">
                  <v-icon x-small color="#082C69" class="me-1">mdi-account-tie-hat</v-icon>
                  {{ selectedTrip.driver ? selectedTrip.driver.name : 'غير محدد' }}
                  <span v-if="selectedTrip.driver && selectedTrip.driver.phone" class="text-caption text-secondary ms-1">
                    ({{ selectedTrip.driver.phone }})
                  </span>
                </span>
              </div>

              <!-- Vehicle Information -->
              <div class="detail-row d-flex align-center justify-space-between mb-2">
                <span class="text-caption text-secondary">المركبة:</span>
                <span class="font-weight-bold text-dark d-flex align-center">
                  <v-icon x-small color="#082C69" class="me-1">mdi-bus</v-icon>
                  {{ selectedTrip.vehicle ? `#${selectedTrip.vehicle.id} - ${selectedTrip.vehicle.plate}` : 'غير محددة' }}
                </span>
              </div>

              <!-- Vehicle Type & Capacity -->
              <div v-if="selectedTrip.vehicle" class="detail-row d-flex align-center justify-space-between mb-2">
                <span class="text-caption text-secondary">النوع والسعة:</span>
                <span class="font-weight-bold text-dark">
                  {{ selectedTrip.vehicle.type }} ({{ selectedTrip.vehicle.capacity }} راكب)
                </span>
              </div>

              <!-- Actual Start vs Scheduled -->
              <div class="detail-row d-flex align-center justify-space-between mb-2">
                <span class="text-caption text-secondary">وقت الانطلاق:</span>
                <span class="font-weight-bold text-dark">
                  {{ selectedTrip.actual_start_time || selectedTrip.first_stop_time_formatted || '—' }}
                </span>
              </div>

              <!-- Speed & Signal -->
              <div class="detail-row d-flex align-center justify-space-between">
                <span class="text-caption text-secondary">حالة الاتصال والسرعة:</span>
                <span class="font-weight-bold text-dark d-flex align-center">
                  <span :class="['freshness-badge me-2', selectedTrip.is_disconnected ? 'fresh-stale' : 'fresh-ok']">
                    {{ selectedTrip.freshness_text }}
                  </span>
                  <span v-if="selectedTrip.location && selectedTrip.location.speed">
                    {{ Math.round(selectedTrip.location.speed) }} كم/س
                  </span>
                </span>
              </div>
            </v-card>
          </v-col>

          <!-- CARD 2: المحطة القادمة -->
          <v-col cols="12" md="4">
            <v-card class="op-detail-card elevation-1 rounded-lg h-100 pa-4">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#0284C7" class="me-2" size="20">mdi-map-marker-star</v-icon>
                  <span class="font-weight-bold text-subtitle-2 text-dark">المحطة القادمة</span>
                </div>
                <v-chip x-small color="#E0F2FE" text-color="#0284C7" class="font-weight-bold">
                  محطة قادمة
                </v-chip>
              </div>

              <template v-if="tripFullDetails && tripFullDetails.next_stop && tripFullDetails.next_stop.exists">
                <div class="next-stop-main mb-3">
                  <div class="d-flex align-center mb-1">
                    <span class="stop-order-pill me-2">#{{ tripFullDetails.next_stop.order }}</span>
                    <span class="text-subtitle-1 font-weight-black text-dark">
                      {{ tripFullDetails.next_stop.name }}
                    </span>
                  </div>
                  <div class="text-caption text-secondary text-truncate" :title="tripFullDetails.next_stop.address">
                    {{ tripFullDetails.next_stop.address || '—' }}
                  </div>
                </div>

                <v-divider class="my-3"></v-divider>

                <!-- ETA & Distance -->
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="stat-box text-center p-2 rounded flex-grow-1 me-2 bg-light-blue">
                    <div class="text-caption text-secondary">المسافة التقريبية</div>
                    <div class="text-h6 font-weight-black text-sky">
                      {{ tripFullDetails.next_stop.distance_formatted || '—' }}
                    </div>
                  </div>
                  <div class="stat-box text-center p-2 rounded flex-grow-1 bg-light-blue">
                    <div class="text-caption text-secondary">الوقت المجدول</div>
                    <div class="text-h6 font-weight-black text-sky">
                      {{ tripFullDetails.next_stop.eta_formatted || '—' }}
                    </div>
                  </div>
                </div>

                <!-- Passenger Boarding / Alighting at Next Stop -->
                <div class="d-flex align-center justify-space-around pt-2 border-top">
                  <div class="d-flex align-center text-caption font-weight-bold text-emerald">
                    <v-icon small color="#059669" class="me-1">mdi-account-arrow-up</v-icon>
                    صعود: {{ tripFullDetails.next_stop.expected_boarding }} ركاب
                  </div>
                  <div class="d-flex align-center text-caption font-weight-bold text-amber">
                    <v-icon small color="#D97706" class="me-1">mdi-account-arrow-down</v-icon>
                    نزول: {{ tripFullDetails.next_stop.expected_alighting }} ركاب
                  </div>
                </div>
              </template>

              <!-- No Next Stop (Trip completed or all stops passed) -->
              <div v-else class="text-center py-8">
                <v-icon size="36" color="#94A3B8" class="mb-2">mdi-flag-checkered</v-icon>
                <div class="text-subtitle-2 font-weight-bold text-secondary mb-1">تم المرور بجميع المحطات</div>
                <div class="text-caption text-muted">الرحلة قاربت على الوصول للمحطة النهائية</div>
              </div>
            </v-card>
          </v-col>

          <!-- CARD 3: المسار الكامل للرحلة -->
          <v-col cols="12" md="4">
            <v-card class="op-detail-card elevation-1 rounded-lg h-100 pa-4 d-flex flex-column" style="max-height: 380px;">
              <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                <div class="d-flex align-center">
                  <v-icon color="#059669" class="me-2" size="20">mdi-timeline-text-outline</v-icon>
                  <span class="font-weight-bold text-subtitle-2 text-dark">المسار الكامل للرحلة</span>
                </div>
                <span class="text-caption font-weight-bold text-secondary">
                  {{ tripFullDetails && tripFullDetails.stops ? tripFullDetails.stops.length : selectedTrip.total_stops_count }} محطة
                </span>
              </div>

              <!-- Scrollable Stops Timeline -->
              <div class="stops-timeline-scroll flex-grow-1 overflow-y-auto pe-2">
                <template v-if="tripFullDetails && tripFullDetails.stops && tripFullDetails.stops.length > 0">
                  <div
                    v-for="(stop, sIdx) in tripFullDetails.stops"
                    :key="'stop-' + (stop.stop_id || sIdx)"
                    :class="['timeline-stop-item', `stop-state-${stop.state}`]"
                  >
                    <!-- Stop Indicator Marker -->
                    <div class="timeline-marker">
                      <v-icon v-if="stop.state === 'completed'" x-small color="white">mdi-check</v-icon>
                      <span v-else-if="stop.state === 'current'" class="current-inner-dot"></span>
                      <span v-else class="text-caption font-weight-bold">{{ stop.order }}</span>
                    </div>

                    <!-- Stop Info -->
                    <div class="timeline-content">
                      <div class="d-flex align-center justify-space-between">
                        <span class="stop-name font-weight-bold text-dark text-truncate">{{ stop.name }}</span>
                        <span class="stop-time text-caption text-secondary">{{ stop.eta_formatted || '—' }}</span>
                      </div>
                      <div class="d-flex align-center justify-space-between text-caption text-muted mt-1">
                        <span class="stop-state-text">{{ stop.state_label }}</span>
                        <span v-if="stop.expected_boarding > 0 || stop.expected_alighting > 0" class="stop-passengers">
                          <span v-if="stop.expected_boarding > 0" class="text-emerald me-1">+{{ stop.expected_boarding }}</span>
                          <span v-if="stop.expected_alighting > 0" class="text-amber">-{{ stop.expected_alighting }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </template>

                <div v-else class="text-center py-8">
                  <v-icon size="36" color="#CBD5E1" class="mb-2">mdi-map-marker-path</v-icon>
                  <div class="text-caption text-secondary">جارٍ تحميل محطات المسار...</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Empty State when no running trip is selected -->
      <v-card v-else class="op-detail-card elevation-1 rounded-lg pa-8 text-center mt-3">
        <v-icon size="48" color="#94A3B8" class="mb-2">mdi-bus-clock</v-icon>
        <div class="text-subtitle-1 font-weight-bold text-dark mb-1">اختر رحلة جارية لعرض تفاصيلها</div>
        <div class="text-caption text-muted">
          {{ runningTrips.length > 0 ? 'اضغط على إحدى الرحلات الجارية في القائمة أعلاه أو على الخريطة لعرض تفاصيلها والمحطة القادمة ومسارها الكامل' : 'لا تتوفر رحلات جارية حالياً في نطاق المجموعات المسندة' }}
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { Keys } from "@/config.js";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";

export default {
  name: "OperationsLiveTracking",
  components: {
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      isPolling: false,
      isLoadingDetails: false,
      pollTimer: null,
      searchTimeout: null,
      googleMapsApiKey: (Keys && Keys.GOOGLE_MAPS_API_KEY) || process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "",

      // Filter selections
      filters: {
        search: "",
        status: "all",
        driver_id: "all",
        vehicle_id: "all",
        group_id: "all",
        date: "",
      },

      // Sidebar Tab: 'all', 'on_time', 'delayed'
      sidebarTab: "all",

      // Overview Data from Backend API
      overview: {
        has_assignments: true,
        kpis: {
          running_count: 0,
          upcoming_count: 0,
          completed_today_count: 0,
          delayed_count: 0,
          disconnected_count: 0,
          active_vehicles_count: 0,
        },
        running_trips: [],
        map_markers: [],
        filter_options: {
          groups: [],
          routes: [],
          drivers: [],
          vehicles: [],
        },
        last_updated_at: null,
        server_time: null,
      },

      // Selected Trip state
      selectedTripId: null,
      tripFullDetails: null,

      // Default Map Center: Cairo operational center
      defaultCenter: {
        lat: parseFloat(Keys && Keys.VUE_APP_ORIGIN_LAT) || 30.0444,
        lng: parseFloat(Keys && Keys.VUE_APP_ORIGIN_LNG) || 31.2357,
      },
      mapZoom: 12,
    };
  },
  computed: {
    lastUpdatedFormatted() {
      if (this.overview.server_time) {
        return this.overview.server_time;
      }
      return new Date().toLocaleTimeString("ar-EG");
    },
    runningTrips() {
      return this.overview.running_trips || [];
    },
    filterOptions() {
      return this.overview.filter_options || {};
    },
    statusOptions() {
      return [
        { id: "all", label: "كل الحالات" },
        { id: "on_time", label: "على الطريق" },
        { id: "delayed", label: "رحلات متأخرة" },
        { id: "disconnected", label: "غير متصلة" },
      ];
    },
    driverOptions() {
      const list = (this.filterOptions.drivers || []).map((d) => ({
        id: d.id,
        label: d.label || d.name,
      }));
      return [{ id: "all", label: "كل السائقين" }, ...list];
    },
    vehicleOptions() {
      const list = (this.filterOptions.vehicles || []).map((v) => ({
        id: v.id,
        label: v.label || `#${v.id} - ${v.license}`,
      }));
      return [{ id: "all", label: "كل المركبات" }, ...list];
    },
    groupOptions() {
      const list = (this.filterOptions.groups || []).map((g) => ({
        id: g.id,
        label: g.label || g.name,
      }));
      return [{ id: "all", label: "كل المجموعات" }, ...list];
    },
    hasActiveFilters() {
      return (
        !!this.filters.search ||
        this.filters.status !== "all" ||
        this.filters.driver_id !== "all" ||
        this.filters.vehicle_id !== "all" ||
        this.filters.group_id !== "all"
      );
    },
    onTimeRunningTripsCount() {
      return this.runningTrips.filter((t) => t.status_key === "on_time").length;
    },
    delayedRunningTripsCount() {
      return this.runningTrips.filter((t) => t.is_delayed).length;
    },
    filteredRunningTrips() {
      let list = this.runningTrips;

      // Filter by sidebar tab
      if (this.sidebarTab === "on_time") {
        list = list.filter((t) => t.status_key === "on_time");
      } else if (this.sidebarTab === "delayed") {
        list = list.filter((t) => t.is_delayed);
      }

      return list;
    },
    selectedTrip() {
      if (!this.selectedTripId) return null;
      return this.runningTrips.find((t) => t.id === this.selectedTripId) || null;
    },
    activeMapMarkers() {
      const markers = [];

      // 1. Vehicle markers:
      // If a single trip is selected, only show that trip's marker
      // Otherwise (default state), show ALL currently executing eligible trips immediately
      const vehicleMarkers = this.selectedTripId
        ? (this.overview.map_markers || []).filter((m) => m.planned_trip_id === this.selectedTripId)
        : (this.overview.map_markers || []);

      vehicleMarkers.forEach((m) => {
        const isSelected = this.selectedTripId === m.planned_trip_id;
        markers.push({
          place_id: `vehicle-${m.planned_trip_id}`,
          trip_id: m.planned_trip_id,
          position: m.position,
          icon: this.createBusMarkerIcon(m, isSelected),
          zIndex: isSelected ? 50 : 10,
          infoText: `
            <div style="font-family: inherit; font-size: 13px; text-align: right; direction: rtl; padding: 4px;">
              <strong>${m.title}</strong><br/>
              السائق: ${m.driver_name}<br/>
              الأتوبيس: ${m.vehicle_plate} (${m.vehicle_type || 'أتوبيس'})<br/>
              الحالة: <span style="color: ${m.status_color}; font-weight: bold;">${m.status_label}</span>
            </div>
          `,
        });
      });

      // 2. If a trip is selected, also add its stop markers
      if (this.selectedTripId) {
        const stopsMarkers = (this.tripFullDetails && this.tripFullDetails.map && this.tripFullDetails.map.stops_markers) || [];
        stopsMarkers.forEach((sm) => {
          markers.push({
            place_id: `stop-${sm.id || sm.order}`,
            position: sm.position,
            icon: sm.icon,
            zIndex: sm.zIndex || 5,
            infoText: `
              <div style="font-family: inherit; font-size: 13px; text-align: right; direction: rtl; padding: 4px;">
                <strong>${sm.title}</strong><br/>
                الوقت المجدول: ${sm.time || '—'}<br/>
                الحالة: ${sm.state_label || '—'}
              </div>
            `,
          });
        });
      }

      return markers;
    },
    activePolylines() {
      if (!this.selectedTripId) return [];
      const poly = this.tripFullDetails && this.tripFullDetails.map && this.tripFullDetails.map.polyline;
      if (poly && poly.length > 0) {
        return [
          {
            data: poly,
            strokeColor: "#082C69",
            strokeOpacity: 0.85,
            strokeWeight: 5,
          },
        ];
      }
      return [];
    },
    mapCenter() {
      // If selected trip has a location, center on it
      if (this.selectedTrip && this.selectedTrip.location && this.selectedTrip.location.lat) {
        return {
          lat: this.selectedTrip.location.lat,
          lng: this.selectedTrip.location.lng,
        };
      }
      // If we have map markers, center on the first one
      if (this.activeMapMarkers.length > 0 && this.activeMapMarkers[0].position) {
        return this.activeMapMarkers[0].position;
      }
      return this.defaultCenter;
    },
  },
  mounted() {
    this.fetchLiveOverview();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    startAutoRefresh() {
      // Safe 20s polling interval
      this.pollTimer = setInterval(() => {
        this.fetchLiveOverview(false, true);
      }, 20000);
    },
    stopAutoRefresh() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 400);
    },
    applyFilters() {
      this.fetchLiveOverview();
    },
    resetFilters() {
      this.filters.search = "";
      this.filters.status = "all";
      this.filters.driver_id = "all";
      this.filters.vehicle_id = "all";
      this.filters.group_id = "all";
      this.sidebarTab = "all";
      this.fetchLiveOverview();
    },
    async fetchLiveOverview(force = false, isPolling = false) {
      if (!isPolling) {
        this.isLoading = true;
      } else {
        this.isPolling = true;
      }

      try {
        const params = {};
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.status !== "all") params.status = this.filters.status;
        if (this.filters.driver_id !== "all") params.driver_id = this.filters.driver_id;
        if (this.filters.vehicle_id !== "all") params.vehicle_id = this.filters.vehicle_id;
        if (this.filters.group_id !== "all") params.group_id = this.filters.group_id;

        const res = await axios.get("/operations/live-tracking", { params });

        if (res.data && res.data.success) {
          this.overview = res.data.data;

          // If a trip is selected, keep it selected and update details if still running; otherwise clear selection
          if (this.selectedTripId) {
            const stillExists = (this.overview.running_trips || []).some((t) => t.id === this.selectedTripId);
            if (stillExists) {
              this.fetchTripDetails(this.selectedTripId, true);
            } else {
              this.selectedTripId = null;
              this.tripFullDetails = null;
            }
          }
        }
      } catch (err) {
        console.error("Live tracking overview fetch error:", err);
      } finally {
        this.isLoading = false;
        this.isPolling = false;
      }
    },
    selectTrip(tripId) {
      // Toggle behavior: clicking the same selected trip clears selection and returns to all trips
      if (this.selectedTripId === tripId) {
        this.selectedTripId = null;
        this.tripFullDetails = null;
        return;
      }
      this.selectedTripId = tripId;
      this.fetchTripDetails(tripId);
    },
    handleMarkerClick(marker) {
      if (marker && marker.trip_id) {
        this.selectTrip(marker.trip_id);
      }
    },
    async fetchTripDetails(tripId, silent = false) {
      if (!silent) {
        this.isLoadingDetails = true;
      }
      try {
        const res = await axios.get(`/operations/live-tracking/trips/${tripId}`);
        if (res.data && res.data.success) {
          this.tripFullDetails = res.data.data;
        }
      } catch (err) {
        console.error("Live trip details fetch error:", err);
      } finally {
        if (!silent) {
          this.isLoadingDetails = false;
        }
      }
    },
    createBusMarkerIcon(m, isSelected) {
      const isDelayed = !!(m.is_delayed || m.status_key === "delayed");
      const isDisconnected = !!(m.is_disconnected || m.status_key === "disconnected");

      let fill = "#059669"; // Normal active: Emerald green
      if (isDelayed) {
        fill = "#D97706"; // Delayed: Amber
      } else if (isDisconnected) {
        fill = "#64748B"; // Disconnected: Slate
      }

      const size = isSelected ? 46 : 36;
      const height = Math.round(size * 1.25);
      const strokeColor = isSelected ? (isDelayed ? "#D97706" : "#082C69") : "#FFFFFF";
      const strokeWidth = isSelected ? 3 : 2.5;
      const tripNum = String(m.planned_trip_code || m.planned_trip_id || m.id || "").slice(-4);

      // Official Material Design mdi-bus path
      const busPath =
        "M18,11H6V6H18M16.5,17A1.5,1.5 0 0,1 15,15.5A1.5,1.5 0 0,1 16.5,14A1.5,1.5 0 0,1 18,15.5A1.5,1.5 0 0,1 16.5,17M7.5,17A1.5,1.5 0 0,1 6,15.5A1.5,1.5 0 0,1 7.5,14A1.5,1.5 0 0,1 9,15.5A1.5,1.5 0 0,1 7.5,17M4,16C4,16.88 4.39,17.67 5,18.22V20A1,1 0 0,0 6,21H7A1,1 0 0,0 8,20V19H16V20A1,1 0 0,0 17,21H18A1,1 0 0,0 19,20V18.22C19.61,17.67 20,16.88 20,16V6C20,2.5 16.42,2 12,2C7.58,2 4,2.5 4,6V16Z";

      const selectionRing = isSelected
        ? `<circle cx="20" cy="20" r="19" fill="none" stroke="${isDelayed ? '#F59E0B' : '#3B82F6'}" stroke-width="2" opacity="0.75"/>`
        : "";

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${height}" viewBox="0 0 40 50">
        ${selectionRing}
        <circle cx="20" cy="20" r="16.5" fill="${fill}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
        <path d="${busPath}" fill="#FFFFFF" transform="translate(8, 8.5)"/>
        <rect x="4" y="36" width="32" height="12" rx="3" fill="#0F172A" opacity="0.92"/>
        <text x="20" y="45" font-size="9" font-weight="bold" fill="#FFFFFF" text-anchor="middle">#${tripNum}</text>
      </svg>`;

      return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize:
          typeof google !== "undefined" && google.maps && google.maps.Size
            ? new google.maps.Size(size, height)
            : { width: size, height },
        anchor:
          typeof google !== "undefined" && google.maps && google.maps.Point
            ? new google.maps.Point(size / 2, size / 2)
            : { x: size / 2, y: size / 2 },
      };
    },
  },
};
</script>

<style scoped>
/* ==================== GENERAL & CONTAINER ==================== */
.operations-live-tracking {
  padding: 18px 24px;
  background-color: #F8FAFC;
  min-height: calc(100vh - 70px);
  font-family: inherit;
}

/* ==================== HEADER ==================== */
.op-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
}

.op-main-title {
  font-size: 24px;
  font-weight: 900;
  color: #0F172A;
  margin: 0 0 4px 0;
}

.op-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 0;
}

.op-badge-section {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background-color: #E2E8F0;
  color: #082C69;
}

.op-live-pill {
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 12px;
  background-color: #DCFCE7;
  color: #15803D;
  border: 1px solid #BBF7D0;
}

.op-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #16A34A;
  display: inline-block;
  animation: pulse-dot 1.8s infinite cubic-bezier(0.4, 0, 0.6, 1);
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.3);
  }
}

.op-scope-pill {
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  background-color: #F0F9FF;
  color: #0284C7;
  border: 1px solid #BAE6FD;
}

.op-no-scope-pill {
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  background-color: #F1F5F9;
  color: #64748B;
  border: 1px solid #CBD5E1;
}

.op-btn-refresh {
  background-color: #FFFFFF !important;
  border: 1px solid #CBD5E1 !important;
  border-radius: 8px;
  font-weight: 700;
  color: #082C69 !important;
  text-transform: none;
  height: 38px;
}

/* ==================== 6 COMPACT KPI CARDS ==================== */
.op-kpi-row {
  margin-left: -6px;
  margin-right: -6px;
}

.op-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  height: 100%;
}

.op-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
}

.kpi-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: #F1F5F9;
}

.kpi-active-buses .kpi-icon-wrap { background-color: #EFF6FF; }
.kpi-disconnected .kpi-icon-wrap { background-color: #F1F5F9; }
.kpi-delayed .kpi-icon-wrap { background-color: #FEF3C7; }
.kpi-completed .kpi-icon-wrap { background-color: #D1FAE5; }
.kpi-upcoming .kpi-icon-wrap { background-color: #E0F2FE; }
.kpi-running .kpi-icon-wrap { background-color: #DBEAFE; }

.kpi-content {
  overflow: hidden;
}

.kpi-value {
  font-size: 20px;
  font-weight: 900;
  line-height: 1.2;
}

.kpi-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-primary { color: #082C69 !important; }
.text-slate { color: #475569 !important; }
.text-amber { color: #D97706 !important; }
.text-emerald { color: #059669 !important; }
.text-sky { color: #0284C7 !important; }
.text-blue { color: #2563EB !important; }

/* ==================== FILTERS ==================== */
.op-filter-card {
  background-color: #FFFFFF;
  border: 1px solid #E2E8F0;
}

.op-filter-input >>> .v-input__control {
  border-radius: 8px;
}

.op-filter-input >>> .v-input__slot {
  min-height: 40px !important;
  font-size: 13px;
}

/* ==================== MAP SECTION ==================== */
.op-map-container {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
}

.op-map-header-bar {
  background: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
}

.op-map-empty-badge {
  position: absolute;
  top: 52px;
  right: 16px;
  z-index: 5;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #CBD5E1;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
}

.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.bg-emerald { background-color: #059669; }
.bg-amber { background-color: #D97706; }
.bg-slate { background-color: #64748B; }

/* ==================== SIDEBAR RUNNING TRIPS ==================== */
.op-trips-sidebar {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
}

.sidebar-tabs {
  background-color: #F1F5F9;
  border-radius: 6px;
  padding: 3px;
}

.sidebar-tab-btn {
  flex: 1;
  padding: 5px 8px;
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.sidebar-tab-btn.active {
  background-color: #FFFFFF;
  color: #082C69;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.op-running-trip-card {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.op-running-trip-card:hover {
  background-color: #F1F5F9;
  border-color: #CBD5E1;
}

.op-running-trip-card.is-selected {
  background-color: #EFF6FF;
  border-color: #082C69;
  box-shadow: 0 0 0 1px #082C69;
}

.op-running-trip-card.is-normal-active {
  border-right: 4px solid #059669 !important;
}

.op-running-trip-card.is-delayed {
  border-right: 4px solid #D97706 !important;
  background-color: #FFFDF5;
}

.op-running-trip-card.is-delayed.is-selected {
  background-color: #FFFBEB !important;
  border-color: #D97706 !important;
  box-shadow: 0 0 0 1px #D97706 !important;
}

.op-running-trip-card.is-disconnected {
  border-right: 4px solid #94A3B8 !important;
}

.op-delay-type-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
}

.badge-delay-start {
  background-color: #FEF3C7;
  color: #92400E;
  border: 1px solid #FCD34D;
}

.badge-delay-exec {
  background-color: #FFEDD5;
  color: #9A3412;
  border: 1px solid #FDBA74;
}

.op-delay-pill-amber {
  background-color: #FEF3C7;
  color: #92400E;
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 9999px;
  white-space: nowrap;
}

.trip-code-pill {
  font-size: 11px;
  background-color: #E2E8F0;
  color: #0F172A;
  padding: 2px 6px;
  border-radius: 4px;
}

.trip-status-pill {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 9999px;
}

.status-on_time {
  background-color: #DCFCE7;
  color: #15803D;
}

.status-delayed {
  background-color: #FEF3C7;
  color: #B45309;
}

.status-disconnected {
  background-color: #F1F5F9;
  color: #475569;
}

/* ==================== BOTTOM 3 CARDS ==================== */
.op-detail-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
}

.bg-light-blue {
  background-color: #F0F9FF;
}

.stop-order-pill {
  font-size: 11px;
  font-weight: 800;
  background-color: #E0F2FE;
  color: #0284C7;
  padding: 2px 6px;
  border-radius: 4px;
}

.freshness-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.fresh-ok {
  background-color: #DCFCE7;
  color: #15803D;
}

.fresh-stale {
  background-color: #FEF3C7;
  color: #B45309;
}

/* ==================== TIMELINE STOPS ==================== */
.timeline-stop-item {
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 16px;
}

.timeline-stop-item:last-child {
  padding-bottom: 4px;
}

.timeline-stop-item::before {
  content: "";
  position: absolute;
  top: 24px;
  right: 11px;
  bottom: 0;
  width: 2px;
  background-color: #E2E8F0;
}

.timeline-stop-item:last-child::before {
  display: none;
}

.timeline-marker {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
}

.stop-state-completed .timeline-marker {
  background-color: #059669;
}

.stop-state-current .timeline-marker {
  background-color: #0284C7;
  box-shadow: 0 0 0 4px #BAE6FD;
}

.current-inner-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #FFFFFF;
}

.stop-state-upcoming .timeline-marker {
  background-color: #E2E8F0;
  color: #64748B;
}

.timeline-content {
  flex-grow: 1;
  overflow: hidden;
}

.stop-name {
  font-size: 13px;
}

.stop-state-text {
  font-size: 11px;
}

.stop-state-completed .stop-state-text {
  color: #059669;
  font-weight: 700;
}

.stop-state-current .stop-state-text {
  color: #0284C7;
  font-weight: 700;
}

/* ==================== EMPTY STATE ==================== */
.op-unassigned-card {
  background: #FFFFFF;
  border: 1px dashed #CBD5E1;
  border-radius: 12px;
  max-width: 600px;
  margin: 40px auto;
}

.op-empty-icon-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-empty-title {
  font-size: 18px;
  font-weight: 800;
  color: #1E293B;
}

.op-empty-desc {
  font-size: 13px;
  color: #64748B;
  line-height: 1.6;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.text-muted { color: #94A3B8; }
.border-bottom { border-bottom: 1px solid #E2E8F0; }
.border-top { border-top: 1px solid #E2E8F0; }
</style>
